import { atom } from 'jotai';
import isEqual from 'lodash/isEqual';

import { CategoryRecord } from '../types/inventoryItem';

export type AttributeDataType =
  | 'Boolean'
  | 'Date'
  | 'Enum'
  | 'Float'
  | 'Integer'
  | 'Text';

export const enumAttributeDataType = {
  Boolean: 'Boolean' as const,
  Date: 'Date' as const,
  Enum: 'Enum' as const,
  Float: 'Float' as const,
  Integer: 'Integer' as const,
  Text: 'Text' as const,
};

export type CategoryAttributeFormState = {
  name: string;
  description: string;
  dataType: AttributeDataType;
  required: boolean;
  validationExpr: string | null;
  options: string[] | null;
};

export type CategoryFormState = {
  name: string;
  description: string;
  attributes: CategoryAttributeFormState[];
};

const initialState = (): CategoryFormState => ({
  name: '',
  description: '',
  attributes: [],
});

const mapRecordToForm = (record: CategoryRecord): CategoryFormState => ({
  name: record.name,
  description: record.description ?? '',
  attributes: record.attributes.map((attr) => ({
    name: attr.name,
    description: attr.description ?? '',
    dataType: (attr.dataType || 'Text') as AttributeDataType,
    required: attr.required,
    validationExpr: attr.validationExpr,
    options: attr.options,
  })),
});

// 🔘🔘🔘 PLAIN ATOMS
export const $categoryForm = atom<CategoryFormState>(initialState());
export const $categoryDiscardBaseline = atom<CategoryFormState | null>(null);
export const $lastPopulatedCategoryId = atom<string | undefined>(undefined);
export const $isCategoryFormPopulated = atom<boolean>(false);

// 🟢🟢🟢 COMPUTED DATA
export const $hasCategoryUnsavedChanges = atom((get) => {
  const form = get($categoryForm);
  const baseline = get($categoryDiscardBaseline);

  if (!baseline) return false;

  return !isEqual(form, baseline);
});

export const $canSubmitCategoryForm = atom((get) => {
  const form = get($categoryForm);
  if (!form.name.trim()) return false;
  for (const attr of form.attributes) {
    if (!attr.name.trim()) return false;
  }
  return true;
});

// 🟠🟠🟠 ACTION -> UPDATE category form
export const $updateCategoryForm = atom(
  null,
  (get, set, update: Partial<CategoryFormState>) => {
    set($categoryForm, { ...get($categoryForm), ...update });
  },
);

export const $addCategoryAttribute = atom(null, (get, set) => {
  const form = get($categoryForm);
  set($categoryForm, {
    ...form,
    attributes: [
      ...form.attributes,
      {
        name: '',
        description: '',
        dataType: enumAttributeDataType.Text,
        required: false,
        validationExpr: null,
        options: null,
      },
    ],
  });
});

export const $removeCategoryAttribute = atom(
  null,
  (get, set, index: number) => {
    const form = get($categoryForm);
    set($categoryForm, {
      ...form,
      attributes: form.attributes.filter((_, i) => i !== index),
    });
  },
);

export const $updateCategoryAttribute = atom(
  null,
  (
    get,
    set,
    {
      index,
      field,
      value,
    }: {
      index: number;
      field: keyof CategoryAttributeFormState;
      value: string | boolean | string[] | null;
    },
  ) => {
    const form = get($categoryForm);
    set($categoryForm, {
      ...form,
      attributes: form.attributes.map((attr, i) =>
        i === index ? { ...attr, [field]: value } : attr,
      ),
    });
  },
);

export const $populateCategoryFormFromRecord = atom(
  null,
  (_get, set, record: CategoryRecord) => {
    const formState = mapRecordToForm(record);
    set($categoryForm, formState);
    set($categoryDiscardBaseline, formState);
    set($lastPopulatedCategoryId, record.id);
    set($isCategoryFormPopulated, true);
  },
);

export const $resetCategoryFormToBaseline = atom(null, (get, set) => {
  const baseline = get($categoryDiscardBaseline);
  if (baseline) {
    set($categoryForm, baseline);
  }
});

export const $populateCategoryFormAction = atom(
  null,
  (get, set, record: CategoryRecord | null | undefined) => {
    if (!record) return;

    const currentRecordId = record.id;
    const lastRecordId = get($lastPopulatedCategoryId);

    if (currentRecordId !== lastRecordId) {
      set($isCategoryFormPopulated, false);
    }

    if (!get($isCategoryFormPopulated)) {
      set($populateCategoryFormFromRecord, record);
    }
  },
);

export const $resetCategoryForm = atom(null, (_get, set) => {
  set($categoryForm, initialState());
  set($categoryDiscardBaseline, null);
  set($lastPopulatedCategoryId, undefined);
  set($isCategoryFormPopulated, false);
});
